import React, { useCallback, useState } from 'react';
import {
  View,
  Image,
  ScrollView,
  StatusBar,
  ActivityIndicator,
  TouchableOpacity,
  StyleSheet,
  RefreshControl,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { SavedListing, getConditionDisplayText } from '@/types/residents/marketplaceListing';
import { COMMUNITY_RESOURCE_ICONS } from '@/assets/admin/CommunityResourceIcons';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { useGetSavedItemsQuery, useToggleSavedItemMutation } from '@/store/api';
import {
  GradientBackground,
  PageHeader,
  GlassCard,
  ProfileNavTabs,
} from '@/components/residents/redesign';

const BookmarkFilledIcon = COMMUNITY_RESOURCE_ICONS.bookmarkFilledIcon;
const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;
const EmptySoldIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_SOLD;

type SavedListingsNavigationProp = StackNavigationProp<RootStackParamList, 'SavedListings'>;
type SavedListingsRouteProp = RouteProp<RootStackParamList, 'SavedListings'>;

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;

export const SavedListingsScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopSavedView />;
  return <MobileSavedView />;
};

function useSavedData() {
  const navigation = useNavigation<SavedListingsNavigationProp>();
  const route = useRoute<SavedListingsRouteProp>();

  const {
    data: savedListings = [],
    isLoading,
    refetch,
  } = useGetSavedItemsQuery();

  const [toggleSavedItem] = useToggleSavedItemMutation();

  useFocusEffect(
    useCallback(() => {
      const params = route.params as { unsavedId?: string } | undefined;
      if (params?.unsavedId) {
        refetch();
        navigation.setParams({ unsavedId: undefined } as any);
      }
    }, [route.params, refetch, navigation])
  );

  const handleListingPress = useCallback((listing: SavedListing) => {
    navigation.navigate('SavedItemDetail', { listing });
  }, [navigation]);

  const handleBookmarkPress = useCallback(async (listing: SavedListing) => {
    try {
      await toggleSavedItem(listing.id).unwrap();
    } catch (error) {
      console.error('Failed to unsave item:', error);
    }
  }, [toggleSavedItem]);

  return { savedListings, isLoading, refetch, handleListingPress, handleBookmarkPress };
}

const SavedContent: React.FC<{
  savedListings: SavedListing[];
  isLoading: boolean;
  onListingPress: (listing: SavedListing) => void;
  onBookmarkPress: (listing: SavedListing) => void;
}> = ({ savedListings, isLoading, onListingPress, onBookmarkPress }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.contentGap}>
      <ProfileNavTabs activeOption="saved" />

      <View style={styles.listingsContainer}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
          </View>
        ) : savedListings.length > 0 ? (
          savedListings.map((listing) => (
            <SavedGlassRow
              key={listing.id}
              listing={listing}
              onPress={() => onListingPress(listing)}
              onBookmarkPress={() => onBookmarkPress(listing)}
            />
          ))
        ) : (
          <View style={styles.emptyContainer}>
            <EmptySoldIcon width={60} height={60} color={colors.text.muted} />
            <Text style={[styles.emptyTitle, { color: colors.text.secondary }]}>No saved items</Text>
            <Text style={[styles.emptySubtitle, { color: colors.text.dim }]}>Items you save will appear here</Text>
          </View>
        )}
      </View>
    </View>
  );
};

const SavedGlassRow: React.FC<{
  listing: SavedListing;
  onPress: () => void;
  onBookmarkPress: () => void;
}> = ({ listing, onPress, onBookmarkPress }) => {
  const { colors } = useThemeColors();
  const imageUri = listing.images?.[0]?.url;
  const conditionText = getConditionDisplayText(listing.condition);

  return (
    <GlassCard interactive onPress={onPress} borderRadius={16} padding={16} style={styles.listingRow}>
      <View style={[styles.listingImageContainer, { borderColor: colors.card.border }]}>
        {imageUri ? (
          <Image source={{ uri: imageUri }} style={styles.listingImage} resizeMode="cover" />
        ) : (
          <View style={[styles.listingPlaceholder, { backgroundColor: colors.card.bg }]}>
            <PropertyPlaceholder width={50} height={50} />
          </View>
        )}
      </View>
      <View style={styles.listingContent}>
        <View style={styles.listingPriceRow}>
          <View style={styles.listingPriceContainer}>
            <Text style={[styles.listingPrice, { color: colors.text.primary }]}>{listing.price}</Text>
            {listing.isSold && (
              <>
                <Text style={[styles.listingPriceDot, { color: colors.text.primary }]}> · </Text>
                <Text style={[styles.listingSoldText, { color: colors.accent.red }]}>Sold</Text>
              </>
            )}
          </View>
          <TouchableOpacity onPress={onBookmarkPress} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <BookmarkFilledIcon width={20} height={20} color={colors.accent.red} />
          </TouchableOpacity>
        </View>
        <Text style={[styles.listingTitle, { color: colors.text.secondary }]} numberOfLines={2}>{listing.title}</Text>
        <View style={[styles.listingConditionBadge, { backgroundColor: colors.accent.greenLight }]}>
          <Text style={[styles.listingConditionText, { color: colors.accent.green }]}>{conditionText}</Text>
        </View>
      </View>
    </GlassCard>
  );
};

const DesktopSavedView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const { savedListings, isLoading, refetch, handleListingPress, handleBookmarkPress } = useSavedData();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => { setRefreshing(true); await refetch(); setRefreshing(false); }, [refetch]);
  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <ScrollView
      style={styles.desktopContainer}
      contentContainerStyle={styles.desktopScrollContent}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />}
    >
      <View style={styles.desktopCentered}>
        <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
        <PageHeader title="Saved" />
        <SavedContent savedListings={savedListings} isLoading={isLoading} onListingPress={handleListingPress} onBookmarkPress={handleBookmarkPress} />
      </View>
    </ScrollView>
  );
};

const MobileSavedView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const { savedListings, isLoading, refetch, handleListingPress, handleBookmarkPress } = useSavedData();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => { setRefreshing(true); await refetch(); setRefreshing(false); }, [refetch]);
  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen screenName="Marketplace" darkBackground headerVariant="gradient">
        <ScrollView
          style={styles.mobileScroll}
          contentContainerStyle={styles.mobileScrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />}
        >
          <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
          <PageHeader title="Saved" />
          <SavedContent savedListings={savedListings} isLoading={isLoading} onListingPress={handleListingPress} onBookmarkPress={handleBookmarkPress} />
        </ScrollView>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  desktopContainer: { flex: 1, backgroundColor: 'transparent' },
  desktopScrollContent: { padding: HP, paddingBottom: 60, alignItems: 'center' },
  desktopCentered: { width: '100%', maxWidth: MAX_CONTENT_WIDTH, gap: 8 },
  mobileScroll: { flex: 1 },
  mobileScrollContent: { paddingHorizontal: HP, paddingBottom: 40, gap: 8 },
  contentGap: { gap: 12 },
  listingsContainer: { gap: 10 },
  loadingContainer: { paddingVertical: 60, alignItems: 'center', justifyContent: 'center' },
  emptyContainer: { alignItems: 'center', gap: 12, paddingVertical: 40 },
  emptyTitle: { ...REDESIGN_TYPOGRAPHY.cardTitleLarge, textAlign: 'center' },
  emptySubtitle: { ...REDESIGN_TYPOGRAPHY.body, textAlign: 'center', maxWidth: 280 },
  listingRow: { flexDirection: 'row', gap: 14 },
  listingImageContainer: { width: 85, height: 85, borderRadius: 12, borderWidth: 1, overflow: 'hidden' },
  listingImage: { width: '100%', height: '100%' },
  listingPlaceholder: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' },
  listingContent: { flex: 1, gap: 3 },
  listingPriceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  listingPriceContainer: { flexDirection: 'row', alignItems: 'center' },
  listingPrice: { ...REDESIGN_TYPOGRAPHY.cardTitle },
  listingPriceDot: { ...REDESIGN_TYPOGRAPHY.cardTitle },
  listingSoldText: { ...REDESIGN_TYPOGRAPHY.cardTitle },
  listingTitle: { ...REDESIGN_TYPOGRAPHY.body },
  listingConditionBadge: { alignSelf: 'flex-start', borderRadius: 4, paddingHorizontal: 7, paddingVertical: 2 },
  listingConditionText: { ...REDESIGN_TYPOGRAPHY.conditionBadge },
});
